/**
 * Personal Hero – Mouse-follow parallax for floating pills.
 *
 * Tracks mouse position over the whole hero block and translates each pill
 * at its own depth, producing a subtle layered parallax effect.
 * Animation is lerped with requestAnimationFrame for smooth GPU-accelerated
 * movement. No-ops on touch-only devices.
 *
 * Markup contract:
 *   <div data-personal-hero-parallax>
 *       …
 *       <span data-parallax-pill data-depth="10">…</span>
 *       <a    data-parallax-pill data-depth="6">…</a>
 *   </div>
 */
export default class PersonalHeroParallax {
  constructor(container) {
    if (!window.matchMedia('(hover: hover)').matches) return;

    this.container = container;
    this.pills = [...container.querySelectorAll('[data-parallax-pill]')];
    if (!this.pills.length) return;

    // Lerp factor — lower = smoother / laggier
    this._ease = 0.055;
    this._targetX = 0;
    this._targetY = 0;
    this._currentX = 0;
    this._currentY = 0;
    this._rafId = null;

    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);

    container.addEventListener('mousemove', this._onMouseMove, { passive: true });
    container.addEventListener('mouseleave', this._onMouseLeave, { passive: true });
  }

  _onMouseMove(e) {
    const rect = this.container.getBoundingClientRect();
    // Normalise to –1 … +1 relative to container centre
    this._targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    this._targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    if (!this._rafId) this._tick();
  }

  _onMouseLeave() {
    this._targetX = 0;
    this._targetY = 0;
    if (!this._rafId) this._tick();
  }

  _tick() {
    this._currentX += (this._targetX - this._currentX) * this._ease;
    this._currentY += (this._targetY - this._currentY) * this._ease;

    this.pills.forEach((pill) => {
      const depth = parseFloat(pill.dataset.depth) || 10;
      const dir = parseFloat(pill.dataset.direction) || 1;
      const x = this._currentX * depth * dir;
      const y = this._currentY * depth * 0.85 * dir;
      pill.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
    });

    const dx = Math.abs(this._targetX - this._currentX);
    const dy = Math.abs(this._targetY - this._currentY);

    if (dx > 0.0005 || dy > 0.0005) {
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this._tick();
      });
    } else {
      this._rafId = null;
      this._currentX = this._targetX;
      this._currentY = this._targetY;
    }
  }

  destroy() {
    this.container.removeEventListener('mousemove', this._onMouseMove);
    this.container.removeEventListener('mouseleave', this._onMouseLeave);
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }
}
